import { useQuery } from "@tanstack/react-query";
import type { ApiError, CatalogueReadinessAggregateView } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase H4 FE-2 — cross-module catalogue readiness hook.
 *
 * Single shell-level fetch consumed by:
 *   - AppShell — disables nav entries for modules whose preconditions
 *     are unmet (timetable / attendance / substitution).
 *   - `<PrerequisiteGate>` — wraps module landing pages and surfaces a
 *     configure-prerequisites UI when the user navigates to a gated
 *     route via deep link, browser back/forward, or because the
 *     disabled-with-tooltip nav entry is still keyboard-focusable.
 *
 * BE counterpart: `GET /me/catalogue-readiness`. The endpoint sets
 * `Cache-Control: no-store` so a freshly-imported student flips the
 * attendance gate on the next render — we keep a 15s TanStack
 * `staleTime` to avoid hammering the BE during nav-heavy admin
 * sessions but accept the round-trip cost when the cache expires.
 *
 * Empty-tenant case: when the tenant has zero branches the BE
 * returns 404 with `code: "READINESS_NO_BRANCH"`. We surface that
 * shape through the regular TanStack `error` channel so callers
 * can render "Finish onboarding first" copy rather than treating
 * it as a hard error.
 */

export const catalogueReadinessKeys = {
  all: ["catalogue-readiness"] as const,
  scoped: (branchId?: string, academicYearId?: string) =>
    [...catalogueReadinessKeys.all, branchId ?? null, academicYearId ?? null] as const,
};

interface UseCatalogueReadinessOptions {
  /** Override the BE's flagship-branch default. Most callers pass
   * `undefined` so the BE picks the right branch — only the manual
   * branch picker needs to override this. */
  branchId?: string;
  /** Override the BE's `{tenant_id}:{calendar_year}` default. */
  academicYearId?: string;
}

export function useCatalogueReadiness(options: UseCatalogueReadinessOptions = {}) {
  const { branchId, academicYearId } = options;
  return useQuery<CatalogueReadinessAggregateView, ApiError>({
    queryKey: catalogueReadinessKeys.scoped(branchId, academicYearId),
    queryFn: async () => {
      const search = new URLSearchParams();
      if (branchId) search.set("branch_id", branchId);
      if (academicYearId) search.set("academic_year_id", academicYearId);
      const query = search.toString();
      const url = query ? `/me/catalogue-readiness?${query}` : "/me/catalogue-readiness";
      return await api.get<CatalogueReadinessAggregateView>(url);
    },
    staleTime: 15_000,
    retry: (failureCount, error) => {
      // 404 READINESS_NO_BRANCH is a structural signal, not a
      // network blip — never retry. Same for any non-5xx — only
      // transient server errors warrant a single retry.
      const status = error?.status ?? 0;
      if (status === 404) return false;
      if (status >= 500 && status < 600) return failureCount < 1;
      return false;
    },
  });
}

/**
 * Stable map of missing-gate code → admin-friendly copy + the
 * deep-link route the FE points the user to when they click
 * "Configure now" on the prerequisite gate.
 *
 * Keep this in lockstep with the BE gate emission in
 * `app/domains/readiness/service.py`. Adding a new gate without a
 * row here is a merge blocker — falling back to a raw snake_case
 * code in admin copy would violate Iron Rule #2 (no placeholders).
 */
export interface ReadinessGateMeta {
  /** Admin-facing label rendered in tooltips and banners. */
  label: string;
  /** Slightly longer one-line description used on the
   * `<PrerequisiteGate>` landing page. */
  description: string;
  /** In-app route that lets the admin satisfy this gate. */
  fixHref: string;
  /** CTA label rendered next to the deep link. */
  fixLabel: string;
}

export const READINESS_GATE_META: Record<string, ReadinessGateMeta> = {
  subjects: {
    label: "No subjects configured",
    description:
      "The catalogue has zero non-archived subjects. The timetable solver and eligibility builder both read from this list.",
    fixHref: "/catalogue/subjects",
    fixLabel: "Open Subjects",
  },
  sections: {
    label: "No sections configured",
    description:
      "The current branch has zero non-archived sections for the active academic year. Every timetable slot and roll-call sheet pivots off a section.",
    fixHref: "/catalogue/sections",
    fixLabel: "Open Sections",
  },
  teacher_eligibility: {
    label: "No teacher eligibility on record",
    description:
      "No teacher has been mapped to a (subject, grade, section) combination yet. The solver picks slot owners from this list.",
    fixHref: "/people",
    fixLabel: "Invite teachers",
  },
  grade_demand: {
    label: "Some grades have no applicable subject",
    description:
      "At least one section's grade has no subject marked applicable. The solver would surface `GRADE_HAS_NO_DEMAND` at submit time.",
    fixHref: "/catalogue/eligibility-matrix",
    fixLabel: "Open coverage matrix",
  },
  students: {
    label: "No active students in this branch",
    description:
      "Attendance roll-call only renders sections that have at least one active student. Import or onboard students first.",
    fixHref: "/people",
    fixLabel: "Manage students",
  },
  class_teachers: {
    label: "No class teachers assigned",
    description:
      "Period-zero attendance scopes to a class teacher. Assign a class teacher to each section before opening the roll-call.",
    fixHref: "/people",
    fixLabel: "Manage staff",
  },
  published_timetable: {
    label: "No published timetable",
    description:
      "Substitution works against a published timetable. Generate, review, and publish a timetable before opening this module.",
    fixHref: "/timetable",
    fixLabel: "Open Timetable",
  },
};

/** Resolve the human-friendly meta for a given gate code; falls back
 * to a synthesised label so an unknown code never breaks the UI.
 * The fallback shape mirrors the structure of a known gate so
 * callers can render without null checks. */
export function readinessGateMeta(code: string): ReadinessGateMeta {
  const known = READINESS_GATE_META[code];
  if (known) return known;
  return {
    label: code.replaceAll("_", " "),
    description:
      "This prerequisite needs configuration before the module unlocks. Open the relevant settings page.",
    fixHref: "/settings",
    fixLabel: "Open Settings",
  };
}
